/**
 * In-app browser detection utilities
 * Detects WebView browsers from social media apps where Google OAuth doesn't work
 */

const IN_APP_PATTERNS = [
  { pattern: /FBAN|FBAV|FB_IAB/i, name: 'Facebook' },
  { pattern: /Instagram/i, name: 'Instagram' },
  { pattern: /\bLine\//, name: 'LINE' },
  { pattern: /\bBarcelona\b/i, name: 'Threads' },
]

export function isInAppBrowser() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  return IN_APP_PATTERNS.some(({ pattern }) => pattern.test(ua))
}

export function detectInAppBrowserName() {
  if (typeof navigator === 'undefined') return null
  const ua = navigator.userAgent || ''
  const match = IN_APP_PATTERNS.find(({ pattern }) => pattern.test(ua))
  return match ? match.name : null
}

/**
 * Try to open current page in external browser.
 * Priority: LINE openExternalBrowser param → Web Share API → null (caller should fallback to copy)
 *
 * @param {string} [customUrl] - Optional URL to open. Defaults to window.location.href
 * @returns {Promise<'redirect'|'shared'|'cancelled'|null>}
 */
export async function openInExternalBrowser(customUrl) {
  const url = stripOpenExternalBrowserParam(customUrl || window.location.href)
  const browserName = detectInAppBrowserName()

  // LINE: official openExternalBrowser=1 param triggers external browser
  if (browserName === 'LINE') {
    const separator = url.includes('?') ? '&' : '?'
    window.location.href = `${url}${separator}openExternalBrowser=1`
    return 'redirect'
  }

  // Web Share API — iOS share sheet has "Open in Safari"
  if (navigator.share) {
    try {
      await navigator.share({ url })
      return 'shared'
    } catch (e) {
      if (e.name === 'AbortError') return 'cancelled'
    }
  }

  // No method available — caller should fallback to copy
  return null
}

function stripOpenExternalBrowserParam(url) {
  try {
    const u = new URL(url)
    u.searchParams.delete('openExternalBrowser')
    return u.toString()
  } catch {
    return url
  }
}
